import type { TFunction } from "react-i18next";
import { isBlank } from "utils/string";
import { isEmptyArray } from "utils/isEmptyArray";
import { FormState } from "../types";

export const NAME_MAX_LENGTH = 100;
export const DESCRIPTION_MAX_LENGTH = 500;

export type FormErrors = {
  name?: string;
  description?: string;
  cloudStorage?: string;
  defaultDefinitions?: Record<string, string>;
  predicates?: Array<{
    variableIdList?: string;
    definitionId?: string;
  }>;
};

export type FormTouched = {
  name?: boolean;
  description?: boolean;
  cloudStorage?: boolean;
  defaultDefinitions?: Record<string, boolean>;
  predicates?: Array<{
    variableIdList?: boolean;
    definitionId?: boolean;
  }>;
};

export function validate(values: FormState, t: TFunction): FormErrors {
  const errors: FormErrors = {};

  if (isBlank(values.name)) {
    errors.name = t("form.name.required");
  } else if (values.name.trim().length > NAME_MAX_LENGTH) {
    errors.name = t("form.name.maxLength", { max: NAME_MAX_LENGTH });
  }

  if (
    !isBlank(values.description) &&
    values.description.length > DESCRIPTION_MAX_LENGTH
  ) {
    errors.description = t("form.description.maxLength", {
      max: DESCRIPTION_MAX_LENGTH,
    });
  }

  if (isBlank(values.cloudStorage)) {
    errors.cloudStorage = t("form.cloudStorage.required");
  }

  const predicateErrors = values.predicates.map(p => {
    const e: FormErrors["predicates"][number] = {};

    if (isEmptyArray(p.variableIdList)) {
      e.variableIdList = t("form.predicates.variableId.required");
    }

    if (isBlank(p.definitionId)) {
      e.definitionId = t("form.predicates.definitionId.required");
    }

    return e;
  });

  if (predicateErrors.some(e => Object.keys(e).length > 0)) {
    errors.predicates = predicateErrors;
  }

  const defaultDefinitions = values.defaultDefinitions || {};
  const defaultErrors: Record<string, string> = {};

  for (const [type, value] of Object.entries(defaultDefinitions)) {
    if (isBlank(value)) {
      defaultErrors[type] = t("form.defaultDefinition.required");
    }
  }

  if (Object.keys(defaultErrors).length > 0) {
    errors.defaultDefinitions = defaultErrors;
  }

  return errors;
}

export function hasErrors(errors: FormErrors): boolean {
  return Object.keys(errors).length > 0;
}
